import { localStorageUtils } from '@/utils/storage/localStorage';
import { STORAGE_KEYS } from '@/constants/storage/storage';
import { logger } from '@/utils/logger/logger';
import { LOGGER_ACTION, LOGGER_FEATURE } from '@/constants/logger/logger-tags';
import { CustomizedBlockPage } from '@/models';
import {
  BLOB_URL_PREFIX,
  DATA_URL_PREFIX,
  PNG_BASE64_PREFIX,
} from '@/constants/ui/images';

export const BLOCK_PAGE_TEXT_MAX_LENGTH = {
  TITLE: 35,
  SUBTITLE: 80,
} as const;
export const MAX_BACKGROUND_SIZE_BYTES = 2 * 1024 * 1024;

export const ALLOWED_BACKGROUND_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
];

export const ACCEPTED_BACKGROUND_TYPES = ALLOWED_BACKGROUND_TYPES.join(',');

export const DEFAULT_CUSTOMIZE_IMAGE =
  '/images/blockpage/backgrounds/category-socialNetworksAndOnlineCommunities-001b.jpg';

export const DEFAULT_CUSTOMIZE_IMAGE_MOBILE =
  '/images/blockpage/custom-block-image-empty-state.png';

export const toUserImageSrc = (image: string): string => {
  if (image.startsWith(BLOB_URL_PREFIX) || image.startsWith(DATA_URL_PREFIX)) {
    return image;
  }
  return `${PNG_BASE64_PREFIX}${image}`;
};

export interface ICustomizeBlockPageForm {
  titleText: string;
  subtitleText: string;
  textColor: string;
  isCustomizedText: boolean;
  hasPickedBackground: boolean;
  hasPendingBackgroundRemoval: boolean;
}

/**
 * The custom block page image bytes are not part of the user-config schema - only the S3
 * key is, and reading that key needs signed credentials the web app does not hold. Caching
 * the bytes locally is what keeps the block page working across reloads until the extension
 * pushes back the copy it read from S3.
 */
export const cacheBlockPageBackground = (dataUrl: string): void => {
  try {
    localStorageUtils.setItem(
      STORAGE_KEYS.CUSTOM_BLOCK_PAGE_BACKGROUND,
      dataUrl
    );
  } catch (error) {
    // a large image can exceed the storage quota, leaving the extension as the only source
    logger.warn('Could not cache the custom block page background', {
      tags: {
        feature: LOGGER_FEATURE.USER,
        action: LOGGER_ACTION.SAVE_CUSTOMIZED_BLOCK_PAGE,
      },
    });
  }
};

export const readCachedBlockPageBackground = (): string => {
  try {
    return localStorageUtils.getObjectItem<string>(
      STORAGE_KEYS.CUSTOM_BLOCK_PAGE_BACKGROUND,
      ''
    );
  } catch (error) {
    return '';
  }
};

export const clearCachedBlockPageBackground = (): void => {
  localStorageUtils.removeItem(STORAGE_KEYS.CUSTOM_BLOCK_PAGE_BACKGROUND);
};

/**
 * A bundle picked in the legacy block page gallery is stored on the same origin and takes
 * priority over the custom background, so changing the background has to discard it.
 */
export const clearSelectedBlockPageBundle = (): void => {
  localStorageUtils.removeItem(STORAGE_KEYS.BLOCK_PAGE_SELECTED_BUNDLE);
};

/**
 * Resetting the background is applied on click, so only a picked-but-not-applied file counts
 * as pending. The texts matter only while the customized text option is on.
 */
export const hasPendingCustomizations = (
  form: ICustomizeBlockPageForm,
  saved: CustomizedBlockPage
): boolean => {
  if (form.hasPickedBackground) return true;
  if (form.hasPendingBackgroundRemoval) return true;
  if (form.isCustomizedText !== saved.isCustomizedText) return true;
  if (!form.isCustomizedText) return false;

  return (
    form.titleText !== saved.titleText ||
    form.subtitleText !== saved.subtitleText ||
    form.textColor !== saved.textColor
  );
};
